import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/_molecules/navigation-controls/link";
import { useDocumentTitle } from "@/hooks/use-document-title";

import { TwoFactorVerificationForm } from "../components/2fa-verification-form";
import { useCompleteLogin } from "../hooks/use-complete-login";
import { exchangeFederatedLoginCode } from "../services/auth.service";
import { takeFederatedReturnPath } from "../services/federated-return-path";

import AuthPageLayout from "./auth-page.layout";

const LOGIN_PATH = "/login";

function readCodeFromFragment(): string | null {
  const code = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("code");
  return code === null || code.length === 0 ? null : code;
}

export function LoginSsoPage() {
  const [hasFailed, setHasFailed] = useState(false);
  const hasSpentLoginCodeRef = useRef(false);
  const { t } = useTranslation("auth");
  const { twoFactorToken, applyOutcome, completeWithReturnPath } = useCompleteLogin({
    resolveReturnPath: takeFederatedReturnPath,
  });
  useDocumentTitle(t("login.title"));

  useEffect(() => {
    if (hasSpentLoginCodeRef.current) {
      return;
    }
    hasSpentLoginCodeRef.current = true;

    const code = readCodeFromFragment();
    if (code === null) {
      setHasFailed(true);
      return;
    }
    window.history.replaceState(null, "", window.location.pathname);

    exchangeFederatedLoginCode(code)
      .then(applyOutcome)
      .catch(() => setHasFailed(true));
  }, [applyOutcome]);

  const handleTwoFactorBack = () => {
    window.location.replace(new URL(LOGIN_PATH, window.location.origin));
  };

  if (twoFactorToken) {
    return (
      <AuthPageLayout>
        <TwoFactorVerificationForm
          twoFactorToken={twoFactorToken}
          onSuccess={completeWithReturnPath}
          onBack={handleTwoFactorBack}
        />
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="flex flex-col items-center justify-center space-y-3 px-4 py-8 text-center sm:px-16">
        {hasFailed ? (
          <>
            <p className="text-sm text-destructive">{t("login.ssoErrors.failed")}</p>
            <Link to="/login" className="text-sm font-semibold">
              {t("login.ssoBackToLogin")}
            </Link>
          </>
        ) : (
          <p className="text-sm">{t("login.ssoCompleting")}</p>
        )}
      </div>
    </AuthPageLayout>
  );
}
